<template>
  <div class="organizationCard updateArn arn">
    <h3>{{ t("title") }}</h3>

    <p class="p1">
      {{ t("subtitle") }}
    </p>

    <div class="arn__keys">
      <ul class="arn__key-list">
        <li class="arn__key-list-item">
          <span class="arn__key-name">{{ t("deployRole") }}</span>
          <span class="arn__key">
            <p class="p1">{{ arn.deployRole }}</p>

            <v-btn @click="() => copyToClipboard(arn.deployRole)" icon>
              <v-icon>mdi-content-copy</v-icon>
            </v-btn>
          </span>
        </li>

        <li class="arn__key-list-item">
          <span class="arn__key-name">{{ t("manageRole") }}</span>

          <span class="arn__key">
            <p class="p1">{{ arn.manageRole }}</p>

            <v-btn @click="() => copyToClipboard(arn.manageRole)" icon>
              <v-icon>mdi-content-copy</v-icon>
            </v-btn>
          </span>
        </li>
      </ul>
    </div>

    <v-form v-if="isUpdatingArn" v-model="validForm" ref="Form" class="wrapInputHandler">
      <div class="inputLabel">
        <span>
          <span>
            {{ t("deployRole") }}
          </span>

          <help-box>
            <v-icon size="16">mdi-help-circle-outline</v-icon>

            <template v-slot:content>
              <span v-html="t('deployRoleTooltip', { link: deployAgentARNLink })" />

              <v-btn @click="copyDeployRoleTrustPolicy" color="primary" class="mt-2">
                {{ t("deployRoleButtons.copyTrustPolicy") }}
              </v-btn>

              <v-btn @click="copyDeployRoleInlinePolicy" color="primary" class="mt-2">
                {{ t("deployRoleButtons.copyInlinePolicy") }}
              </v-btn>
            </template>
          </help-box>
        </span>

        <v-text-field
          v-model="deployRoleArn"
          @change="forceValidateForm"
          :disabled="loader.on"
          :rules="[rules.validateArn]"
          rbt-target="newDeployRoleArn"
        />
      </div>

      <div class="inputLabel">
        <span>
          <span>{{ t("manageRole") }}</span>

          <help-box>
            <v-icon size="16">mdi-help-circle-outline</v-icon>

            <template v-slot:content>
              <span v-html="t('manageRoleTooltip', { link: vizNowARNLink })" />

              <v-btn @click="copyManageRoleInlinePolicy" color="primary" class="mt-2">
                {{ t("manageRoleButtons.copyInlinePolicy") }}
              </v-btn>
            </template>
          </help-box>
        </span>

        <v-text-field
          v-model="manageRoleArn"
          @change="forceValidateForm"
          :disabled="loader.on"
          :rules="[rules.validateArn, rules.unique]"
          rbt-target="newManageRoleArn"
        />
      </div>

      <div class="controller">
        <v-btn @click="isUpdatingArn = false" :disabled="loader.on" rbt-target="cancelChangeBtn" text large>
          {{ t("cancelEdit") }}
        </v-btn>

        <v-btn
          @click="changeArn"
          :disabled="!isValidArn || !validForm || loader.on"
          rbt-target="saveArnButton"
          color="primary"
          large
        >
          {{ t("saveArn") }}
        </v-btn>
      </div>
    </v-form>

    <div v-if="!isUpdatingArn" class="arn-edit">
      <v-btn @click="isUpdatingArn = true" :disable="loader.on" large>{{ t("editRoles") }}</v-btn>
      <v-btn @click="validateRoles" :loading="loader.on" color="primary" large>{{ t("validateRoles") }}</v-btn>
    </div>

    <a :href="LINK_ARN_GUIDE" target="_blank" class="link">
      <v-icon>status_info</v-icon>
      <span>{{ t("howToUpdate") }}</span>
    </a>
  </div>
</template>

<script lang="ts">
import { ref, defineComponent, computed, reactive } from "@vue/composition-api"
import { createTranslationModule } from "@/plugins/i18n"
import { copyToClipboard } from "@/utils/ui"
import { extractAccountId, validateArn } from "@/views/onboarding/lib/arn-validation"
import { VForm } from "@/types/core"
import { raiseSuccess } from "@/utils/event-bus"
import { LINK_ARN_GUIDE } from "@/config/constants"
import { useStore } from "@/composable/useStore"
import Loader from "@/utils/loader"
import ArnCodeSnippets from "@/views/onboarding/lib/copy-snippet"
import HelpBox from "@/components/helpBox/HelpBox.vue"
import ArnLinks from "./lib/arn-links"

export default defineComponent({
  name: "UpdateArn",
  setup() {
    const { licenseModule, organizationModule } = useStore()
    const t = createTranslationModule("OrganizationPageV2.arnUpdate")
    const loader = Loader()
    const Form = ref<VForm>()
    const validForm = ref(false)
    const isUpdatingArn = ref(false)
    const deployRoleArn = ref("")
    const manageRoleArn = ref("")

    const arn = reactive({
      deployRole: "abc123-arn-123-deployRole",
      manageRole: "abc123-arn-123-manageRole"
    })

    const organization = computed(() => {
      return organizationModule.organizationDetail
    })

    const awsExternalId = computed(() => {
      return ArnLinks.uuid
    })

    const isValidArn = computed(() => {
      const validDeployRole = validateArn(deployRoleArn.value) === true
      const validManageRole = validateArn(manageRoleArn.value) === true

      if (validDeployRole && validManageRole) {
        return deployRoleArn.value !== manageRoleArn.value
      }
      return false
    })

    const deployAgentARNLink = computed(() => {
      return ArnLinks.deployAgentArn()
    })

    const vizNowARNLink = computed(() => {
      return ArnLinks.vizNowArn()
    })

    const copyDeployRoleTrustPolicy = () => {
      copyToClipboard(ArnCodeSnippets.trustPolicy)
    }

    const copyDeployRoleInlinePolicy = () => {
      copyToClipboard(ArnCodeSnippets.inlinePolicy)
    }

    const copyManageRoleInlinePolicy = () => {
      copyToClipboard(ArnCodeSnippets.vizNowTabInlinePolicy)
    }

    const changeArn = () => {
      const awsDeployAccountId = extractAccountId(deployRoleArn.value)

      loader.run(async () => {
        await licenseModule.updateArns({
          organizationId: organization.value.id,
          spaceliftAwsRoleArn: deployRoleArn.value,
          vizrtAwsRoleArn: manageRoleArn.value,
          vizrtAwsRoleExternalId: awsExternalId.value,
          awsDeployAccountId
        })

        isUpdatingArn.value = false

        await organizationModule.getOrganizationDetail(organization.value.id)
        _fillArnFromOrganization()

        raiseSuccess({ text: t("successWarning") })
      })
    }

    const validateRoles = () => {
      loader.run(async () => {
        const organizationId = organization.value.id
        await licenseModule.validateIAMRole(organizationId)

        raiseSuccess({ text: t("completeValidation") })
      })
    }

    const forceValidateForm = () => {
      Form.value?.validate()
    }

    const _fillArnFromOrganization = () => {
      if (organization.value?.id) {
        arn.deployRole = organization.value?.cloudProvider?.deployIamRole || ""
        arn.manageRole = organization.value?.cloudProvider?.managementIamRole || ""

        if (!arn.deployRole || !arn.manageRole) {
          throw Error("ARNs not found")
        }
      }
    }

    _fillArnFromOrganization()

    return {
      arn,
      copyToClipboard,
      isUpdatingArn,
      validateRoles,
      changeArn,
      copyDeployRoleTrustPolicy,
      copyDeployRoleInlinePolicy,
      copyManageRoleInlinePolicy,
      deployAgentARNLink,
      deployRoleArn,
      forceValidateForm,
      Form,
      isValidArn,
      loader,
      manageRoleArn,
      t,
      validForm,
      vizNowARNLink,
      LINK_ARN_GUIDE,
      rules: {
        validateArn: (value: string) => validateArn(value, t("ruleFeedback.invalid")),
        unique: (value: string) => value !== deployRoleArn.value || t("ruleFeedback.notUnique")
      }
    }
  },
  components: {
    HelpBox
  }
})
</script>
